import React from "react";
import { CircleNotch } from "@phosphor-icons/react";

/**
 * iOS in-app purchase card — Apple Guideline 3.1.2(c) compliant,
 * styled to match the TipLingo V2 design language.
 *
 * Layout:
 *   • iPhone (default): vertical stack — title, length, price/trial,
 *     savings/flex, auto-renew, full-width button.
 *   • iPad / >=sm breakpoint: horizontal 3-column layout — title block |
 *     price + disclosures | large embedded-price CTA button.
 *
 * Both layouts surface the same Apple-required disclosures BEFORE the
 * Apple purchase sheet opens:
 *   - Subscription title (e.g. "TipLingo Pro Yearly")
 *   - Subscription length ("12 months · auto-renewable")
 *   - Trial length (only when ASC has a real intro free trial configured)
 *   - Exact post-trial billed amount — same weight as the trial copy AND
 *     re-stated inside the action button
 *   - Auto-renewal language
 *
 * The "Best Value" badge sits as a rounded pill on the top-left edge of
 * the yearly card, slightly overlapping the orange ring border for the
 * V2 elevated-pill look.
 */
export default function PaywallIOSPriceCard({
  pkg,
  busy,
  onBuy,
  isBestValue = false,
  savingsLabel = null,
  flexLabel = null,
}) {
  const period = pkg.period || "purchase";
  const isYearly = period === "year";
  // T49.T (Build 81) — Apple 3.1.2(c) rejection fix. The ASC
  // subscription products do not currently have an intro free trial
  // offer configured. Until they do, every paywall surface must hide
  // trial copy. We intentionally ignore `pkg.intro_trial_label` here
  // and force the simple price line + plain Subscribe CTA.
  const priceUnit = `${pkg.price_string}/${period}`;
  const title =
    pkg.title ||
    (isYearly ? "TipLingo Pro Yearly" : "TipLingo Pro Monthly");
  const lengthLabel = isYearly
    ? "12 MONTHS · AUTO-RENEWABLE"
    : "1 MONTH · AUTO-RENEWABLE";
  const priceLine = priceUnit;
  const buttonLabel = isYearly
    ? `Subscribe Yearly — ${priceUnit}`
    : `Subscribe Monthly — ${priceUnit}`;

  return (
    <div
      data-testid={`ios-card-${pkg.identifier}`}
      className={`relative bg-white rounded-2xl shadow-soft border ${
        isBestValue
          ? "border-orange border-2"
          : "border-navy/10"
      } p-5 sm:p-6 sm:pt-6`}
    >
      {isBestValue && (
        <div
          data-testid={`ios-card-${pkg.identifier}-best-value`}
          className="absolute -top-3 left-5 bg-orange text-white text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-extrabold font-jakarta px-3 py-1 rounded-full shadow-soft"
        >
          Best Value
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1.4fr)] gap-4 sm:gap-5 sm:items-center">
        {/* Column 1 — Title + length */}
        <div className="min-w-0">
          <div className="font-jakarta font-extrabold text-navy text-lg sm:text-xl leading-tight">
            {title}
          </div>
          <div className="text-navy-soft text-[11px] uppercase tracking-[0.18em] font-jakarta font-bold mt-1">
            {lengthLabel}
          </div>
        </div>

        {/* Column 2 — Trial line, savings/flex, auto-renew */}
        <div className="min-w-0 sm:border-l sm:border-navy/10 sm:pl-5">
          <div
            data-testid={`ios-card-${pkg.identifier}-price-line`}
            className="font-jakarta font-extrabold text-navy text-base sm:text-[15px] leading-snug"
          >
            {priceLine}
          </div>
          {savingsLabel && (
            <div className="text-forest font-jakarta font-extrabold text-xs mt-1">
              {savingsLabel}
            </div>
          )}
          {flexLabel && !savingsLabel && (
            <div className="text-navy-soft font-jakarta font-medium text-xs italic mt-1">
              {flexLabel}
            </div>
          )}
          <div className="text-navy-soft font-jakarta font-medium text-[11px] leading-snug mt-1.5">
            Auto-renews unless canceled.
          </div>
        </div>

        {/* Column 3 — CTA button with embedded post-trial price */}
        <div className="min-w-0">
          <button
            type="button"
            data-testid={`buy-ios-${pkg.identifier}`}
            onClick={onBuy}
            disabled={busy}
            className="w-full bg-orange hover:bg-orange-hover text-white font-jakarta font-extrabold text-sm leading-snug py-3 px-4 rounded-2xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-center shadow-soft"
          >
            {busy && (
              <CircleNotch
                size={14}
                weight="bold"
                className="animate-spin shrink-0"
              />
            )}
            <span className="whitespace-pre-line">
              {formatButtonTwoLine(buttonLabel)}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// T49.T (Build 81) — Apple 3.1.2(c). Kept for future restoration if an
// ASC intro free-trial offer is approved. Unused while trial copy is
// stripped from every paywall surface.
// eslint-disable-next-line no-unused-vars
function formatTrialButton(label) {
  const m = /^(\d+)\s+(day|week|month|year)s?\s+free$/i.exec(label || "");
  if (!m) return label;
  const unit = m[2].charAt(0).toUpperCase() + m[2].slice(1).toLowerCase();
  return `${m[1]}-${unit} Free`;
}

// Split the long button label across two lines at the em-dash so the
// "Start 7-Day Free Trial" headline and "— then $X/period" pricing each
// get their own visual line — matches the V2 design mock without forcing
// extremely small text on iPhone.
function formatButtonTwoLine(label) {
  const idx = label.indexOf("— ");
  if (idx === -1) return label;
  return label.slice(0, idx).trim() + " —\n" + label.slice(idx + 2).trim();
}
